import { BumpersPlan } from '@utils/bump';
import { toMomentDate } from 'shovel-lib/utils/timeUtils';
import { blockEl } from './floatToNow';
import { CALENDAR_DAY_START_HOUR } from './calendarConstants';

/**
 * While a session is dragged with Bumpers on (timer spec "Bumpers: dragging bumps the chain", Petr 2026-10-19), the
 * sessions it would bump slide live to where they'd land, onto the next days on screen too, showing their new times;
 * split pieces and a dragged session that snaps past a class show as copies where they'd go; a tag by the pointer says
 * what the drop will do. Study sessions get bumper ends for as long as the drag lasts.
 *
 * All of it is drawn straight on the calendar's blocks, not through the store: FullCalendar redraws every block when
 * its wrapper re-renders. The moves are put on the blocks' harnesses, which is where floatToNow measures a block, so the
 * drop's save (state/calendar/bumpers.ts) slides them on from where they were seen.
 */

// On document.body, which neither React nor FullCalendar redraws (calendarStyles).
const DRAG_CLASS = 'bumpers-drag';
const SNAPPED_CLASS = 'bumpers-snapped';
const RESIZED_CLASS = 'bumpers-resized';
const MOVING_CLASS = 'bumpers-moving';
const AWAY_CLASS = 'bumpers-away';
const RETIMED_CLASS = 'bumpers-retimed';
const TIME_CLASS = 'bumpers-time';
export const BUMPERS_GHOST_CLASS = 'bumpers-ghost';
// The copy where a dragged session that snaps will land, drawn as a dotted outline (calendarStyles).
const LANDING = 'dragged';
const LANDING_CLASS = 'bumpers-landing';
const DUE_CHIP_CLASS = 'bumpers-due-chip';

const DAY_START_MS = CALENDAR_DAY_START_HOUR * 60 * 60 * 1000;

type Shown = { harness: HTMLElement; el: HTMLElement };

let calendar: HTMLElement | null = null;
let time24 = true;
const shown = new Map<string, Shown>();
let tag: HTMLDivElement | null = null;
let pointer = { x: -1000, y: -1000 };

const placeTag = () => {
  if (tag) tag.style.transform = `translate(${pointer.x + 16}px, ${pointer.y - 34}px)`;
};
let onLeave: (() => void) | undefined;
let inside = true;

// Off the time grid (onto the sidebar, off the page as Esc does it) nothing would be bumped: the preview goes, and
// comes back from FullCalendar's next eventAllow once the pointer is back over the grid.
const overGrid = (x: number, y: number) => {
  // The time grid in week and day views (the all-day row above it comes first in the page), else the month's grid.
  const body = (calendar?.querySelector('.fc-timegrid-body') ||
    calendar?.querySelector('.fc-daygrid-body')) as HTMLElement | null;
  const box = (body || calendar)?.getBoundingClientRect();
  return !!box && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
};

const followPointer = (e: PointerEvent | MouseEvent | DragEvent) => {
  if (!e.clientX && !e.clientY) return;
  // Each move of a mouse comes as a pointermove and then a mousemove: the second has nothing new.
  if (e.type === 'mousemove' && e.clientX === pointer.x && e.clientY === pointer.y) return;
  pointer = { x: e.clientX, y: e.clientY };
  placeTag();
  const over = overGrid(e.clientX, e.clientY);
  if (inside && !over) {
    showBumpersPreview(undefined, { start: 0, end: 0 });
    onLeave?.();
  }
  inside = over;
};

/** Starts the look of a Bumpers drag on the calendar `within` is in. */
export const startBumpersPreview = (within: Element | null, options: { time24: boolean; onLeave?: () => void }) => {
  endBumpersPreview();
  calendar = ((within && within.closest('.fc')) || document.querySelector('.fc')) as HTMLElement | null;
  if (!calendar) return;
  time24 = options.time24;
  onLeave = options.onLeave;
  inside = true;
  document.body.classList.add(DRAG_CLASS);
  document.addEventListener('pointermove', followPointer, true);
  document.addEventListener('mousemove', followPointer, true);
  document.addEventListener('dragover', followPointer, true);
};

export const isBumpersPreviewOn = () => !!calendar;
/** Whether the pointer was over the time grid when last seen (a drop there lands on the calendar). */
export const isPointerOverGrid = () => !!calendar && inside;

/** The time a block shows, as the calendar writes it. */
const timeLabel = (start: number, end: number) => {
  const from = toMomentDate(new Date(start));
  const to = toMomentDate(new Date(end));
  return time24 ? `${from.format('HH:mm')} - ${to.format('HH:mm')}` : `${from.format('h:mm')} - ${to.format('h:mma')}`;
};

const retime = (el: HTMLElement, start: number, end: number) => {
  const original = el.querySelector(`.fc-event-time-text:not(.${TIME_CLASS})`);
  if (!original) return;
  let mine = el.querySelector(`.${TIME_CLASS}`) as HTMLElement | null;
  if (!mine) {
    mine = document.createElement('span');
    mine.className = `fc-event-time-text ${TIME_CLASS}`;
    original.after(mine);
  }
  const label = timeLabel(start, end);
  if (mine.textContent !== label) mine.textContent = label;
  el.classList.add(RETIMED_CLASS);
};

const unretime = (el: HTMLElement) => {
  el.classList.remove(RETIMED_CLASS);
  el.querySelectorAll(`.${TIME_CLASS}`).forEach(node => node.remove());
};

/** The column a moment is drawn in (the calendar's days run 4am to 4am), when it's on screen. */
const columnAt = (at: number) => {
  const date = toMomentDate(new Date(at - DAY_START_MS)).format('YYYY-MM-DD');
  return calendar?.querySelector(`.fc-timegrid-col[data-date="${date}"] .fc-timegrid-col-events`) as HTMLElement | null;
};

const minutesOf = (time: string | null) => {
  const [hours, minutes] = (time || '0:0').split(':').map(Number);
  return hours * 60 + minutes;
};

/** Where the time grid's rows are on screen: the first row's top, pixels per minute, the minute it starts at. */
const grid = () => {
  const lanes = calendar?.querySelectorAll('.fc-timegrid-slot-lane');
  if (!lanes || lanes.length < 2) return undefined;
  const first = lanes[0] as HTMLElement;
  const second = lanes[1] as HTMLElement;
  const step = minutesOf(second.getAttribute('data-time')) - minutesOf(first.getAttribute('data-time'));
  const top = first.getBoundingClientRect().top;
  const perMinute = step > 0 ? (second.getBoundingClientRect().top - top) / step : 0;
  return perMinute > 0 ? { top, perMinute, from: minutesOf(first.getAttribute('data-time')) } : undefined;
};

/** How far down the grid a moment is, on screen. */
const yAt = (at: number, rows: NonNullable<ReturnType<typeof grid>>) => {
  const moment = toMomentDate(new Date(at));
  let minutes = moment.hours() * 60 + moment.minutes();
  if (minutes < rows.from) minutes += 24 * 60;
  return rows.top + (minutes - rows.from) * rows.perMinute;
};

// The red due pills on the sessions held by their due dates (calendarStyles), by session.
const dueChips = new Map<string, HTMLElement>();

const showDueChips = (held: { id: string; label: string }[]) => {
  const keep = new Set(held.map(it => it.id));
  dueChips.forEach((chip, id) => {
    if (keep.has(id) && chip.isConnected) return;
    chip.remove();
    dueChips.delete(id);
  });
  held.forEach(({ id, label }) => {
    let chip = dueChips.get(id);
    // Still on its block from the step before: left as it is, so the drag's next step reads the layout unchanged.
    if (chip?.isConnected && chip.textContent === label) return;
    const el = blockEl(id);
    if (!el) return;
    if (!chip) {
      chip = document.createElement('span');
      chip.className = DUE_CHIP_CLASS;
      dueChips.set(id, chip);
    }
    if (chip.textContent !== label) chip.textContent = label;
    if (chip.parentElement !== el) el.appendChild(chip);
  });
};

// The copies on the calendar, by what each shows ("move:12", "piece:12:0", "dragged"). Kept from one place of the
// drag to the next and moved, not drawn afresh: drawn afresh at every step, each faded in again, and the sessions they
// show blinked as the pointer moved (Petr, 2026-10-20).
let ghosts = new Map<string, HTMLElement>();
let drawn = new Set<string>();

const clearGhosts = () => {
  ghosts.forEach(ghost => ghost.remove());
  ghosts = new Map();
};

/** A copy of `source` drawn at `start`–`end`, for a piece or a block whose length changes; `key` says which copy. */
const drawGhost = (
  key: string,
  source: HTMLElement,
  start: number,
  end: number,
  rows: NonNullable<ReturnType<typeof grid>>
) => {
  const column = columnAt(start);
  if (!column) return;
  drawn.add(key);
  let wrap = ghosts.get(key);
  if (!wrap) {
    wrap = document.createElement('div');
    wrap.className = `fc-timegrid-event-harness ${BUMPERS_GHOST_CLASS}${key === LANDING ? ` ${LANDING_CLASS}` : ''}`;
    wrap.style.cssText = 'position:absolute;left:0;right:2%;z-index:5;pointer-events:none;';
    const copy = source.cloneNode(true) as HTMLElement;
    // Never found as the block itself (floatToNow's blockEl), nor sharing its id.
    [copy, ...Array.from(copy.querySelectorAll('[data-planned-task-id], [id]'))].forEach(node => {
      (node as HTMLElement).removeAttribute('data-planned-task-id');
      (node as HTMLElement).removeAttribute('id');
    });
    copy.classList.remove('fc-event-dragging', 'fc-event-mirror', 'fc-event-selected', RETIMED_CLASS, AWAY_CLASS);
    copy.style.position = 'absolute';
    copy.style.inset = '0';
    copy.style.height = 'auto';
    copy.querySelectorAll(`.${TIME_CLASS}`).forEach(node => node.remove());
    wrap.appendChild(copy);
    ghosts.set(key, wrap);
  }
  if (wrap.parentElement !== column) column.appendChild(wrap);
  wrap.style.top = `${yAt(start, rows) - column.getBoundingClientRect().top}px`;
  wrap.style.height = `${Math.max(8, ((end - start) / 60000) * rows.perMinute - 2)}px`;
  const copy = wrap.firstElementChild as HTMLElement | null;
  if (copy) retime(copy, start, end);
};

const putBack = (id: string, keepAway = false) => {
  const it = shown.get(id);
  if (!it) return;
  it.harness.style.transform = '';
  // A block that faded as it goes off screen, dropped: hidden until the calendar's redraw takes it away, or it showed
  // in its old place for a moment. Shown again should the redraw keep it.
  if (keepAway && it.harness.classList.contains(AWAY_CLASS)) {
    const { harness } = it;
    window.setTimeout(() => harness.classList.remove(AWAY_CLASS), 800);
  } else {
    it.harness.classList.remove(AWAY_CLASS);
  }
  unretime(it.el);
  shown.delete(id);
};

/**
 * Shows what the drop would do: `plan` for the dragged session, `dragged` where the pointer has it, and on each session
 * held by its due date (`held`) a pill saying when it's due. With no plan the sessions go back to their places and the
 * tag and pills hide.
 */
export const showBumpersPreview = (
  plan: BumpersPlan | undefined,
  dragged: { id?: string; start: number; end: number },
  tagText?: string,
  held: { id: string; label: string }[] = []
) => {
  if (!calendar) return;
  showDueChips(plan && plan.kind !== 'refused' ? held : []);
  const rows = grid();
  const moving = new Map<string, { start: number; end: number }>();
  if (plan && plan.kind !== 'refused') plan.moves.forEach(move => moving.set(move.id, move));
  Array.from(shown.keys())
    .filter(id => !moving.has(id))
    .forEach(id => putBack(id));
  drawn = new Set();

  // All measured first, then all moved: a size read after a move makes the browser lay the calendar out again, for each
  // block at each new place of the drag.
  const blocks = Array.from(moving.entries()).map(([id, move]) => {
    const el = blockEl(id);
    const harness = el?.parentElement;
    if (!el || !harness || !rows) return undefined;
    const from = harness.parentElement;
    const to = columnAt(move.start);
    const height = ((move.end - move.start) / 60000) * rows.perMinute;
    // Off screen, or a different length (split, so its first piece is shorter): it fades, and a copy shows the piece.
    const slides = !!from && !!to && Math.abs(height - harness.offsetHeight) <= 3;
    const shift = slides
      ? {
          x: to!.getBoundingClientRect().left - from!.getBoundingClientRect().left,
          y: yAt(move.start, rows) - to!.getBoundingClientRect().top - harness.offsetTop
        }
      : undefined;
    return { id, move, el, harness, to, shift };
  });
  blocks.forEach(block => {
    if (!block || !rows) return;
    const { id, move, el, harness, to, shift } = block;
    shown.set(id, { el, harness });
    harness.classList.add(MOVING_CLASS);
    if (!shift) {
      harness.classList.add(AWAY_CLASS);
      if (to) drawGhost(`move:${id}`, el, move.start, move.end, rows);
      return;
    }
    harness.classList.remove(AWAY_CLASS);
    harness.style.transform = `translate(${Math.round(shift.x)}px, ${Math.round(shift.y)}px)`;
    retime(el, move.start, move.end);
  });

  if (plan && rows && plan.kind !== 'refused') {
    const source = (dragged.id && blockEl(dragged.id)) || (calendar.querySelector('.fc-event-mirror') as HTMLElement);
    const pieceCount = new Map<string, number>();
    plan.creates.forEach(piece => {
      const index = pieceCount.get(piece.fromId) ?? 0;
      pieceCount.set(piece.fromId, index + 1);
      const el = blockEl(piece.fromId);
      if (el) drawGhost(`piece:${piece.fromId}:${index}`, el, piece.start, piece.end, rows);
    });
    // Snapped past what it was dropped on, or another length (shortened, or filling free time): a copy shows where it
    // goes; the block under the pointer fades, or goes while the copy starts where it does, their words overlapping.
    const moved = plan.start !== dragged.start || plan.end !== dragged.end;
    document.body.classList.toggle(SNAPPED_CLASS, moved);
    document.body.classList.toggle(RESIZED_CLASS, moved && plan.start === dragged.start);
    if (moved && source) drawGhost(LANDING, source, plan.start, plan.end, rows);
  } else {
    document.body.classList.remove(SNAPPED_CLASS, RESIZED_CLASS);
  }
  ghosts.forEach((ghost, key) => {
    if (drawn.has(key)) return;
    ghost.remove();
    ghosts.delete(key);
  });

  if (tagText) {
    if (!tag) {
      tag = document.createElement('div');
      tag.setAttribute('role', 'status');
      tag.style.cssText =
        'position:fixed;left:0;top:0;z-index:100000;pointer-events:none;padding:3px 10px;border-radius:10px;' +
        'font-size:12px;font-weight:700;line-height:18px;white-space:nowrap;color:#fff;' +
        'box-shadow:0 2px 8px rgba(0,0,0,0.25);transition:background-color .15s ease;';
      document.body.appendChild(tag);
    }
    tag.textContent = tagText;
    tag.style.backgroundColor = plan?.kind === 'refused' ? '#C0392B' : '#3C3489';
    placeTag();
  } else if (tag) {
    tag.remove();
    tag = null;
  }
};

/**
 * Ends the drag's look: the blocks go back to the calendar's own drawing, the tag goes. Cancelled, they slide back.
 * Handed off to a drop's save (`dropped`), the moves come off at once, as floatToNow takes over from where the blocks
 * are seen, and the copies stay until the save draws the pieces (`clearBumpersGhosts`).
 */
export const endBumpersPreview = (options: { dropped?: boolean; keepLook?: boolean } = {}) => {
  document.removeEventListener('pointermove', followPointer, true);
  document.removeEventListener('mousemove', followPointer, true);
  document.removeEventListener('dragover', followPointer, true);
  Array.from(shown.keys()).forEach(id => {
    const it = shown.get(id);
    if (!it) return;
    if (options.dropped) it.harness.classList.remove(MOVING_CLASS);
    putBack(id, options.dropped);
    if (!options.dropped) window.setTimeout(() => it.harness.classList.remove(MOVING_CLASS), 400);
  });
  if (!options.dropped) clearGhosts();
  // Dropped, the session itself slides to where its outline was.
  ghosts.get(LANDING)?.remove();
  ghosts.delete(LANDING);
  tag?.remove();
  tag = null;
  showDueChips([]);
  document.body.classList.remove(SNAPPED_CLASS, RESIZED_CLASS);
  // `keepLook`: sessions keep their bumper ends and full strength until a later call, as a task dropped from the
  // sidebar is still being dragged until the calendar has taken it (the drag's fade showed meanwhile, Petr 2026-10-24).
  if (options.keepLook) return;
  document.body.classList.remove(DRAG_CLASS);
  calendar = null;
  onLeave = undefined;
};

export const clearBumpersGhosts = clearGhosts;
